export const CONTENT_SCHEMA_VERSION = 1 as const;

export type ContentSchemaVersion = typeof CONTENT_SCHEMA_VERSION;
export type LevelId = string;
export type DialogueId = string;
export type EntityTypeId = string;
export type EncounterId = string;
export type GeneratorId = string;
export type WeaponId = string;

/** Grid coordinates in 64px level cells. Half values denote cell centres. */
export interface GridPoint {
  readonly x: number;
  readonly y: number;
}
/** Pixel-space offset used by sprite-local authored tuning. */
export interface PixelPoint {
  readonly x: number;
  readonly y: number;
}

export interface ShotChannelDocument {
  readonly id: string;
  readonly counter: number;
  readonly limit: number;
  readonly repeatLimit: number;
  readonly repeatSpacing?: number;
}

export interface ShotWaveDocument {
  readonly channels: readonly ShotChannelDocument[];
}

export interface GameManifestDocument {
  readonly schemaVersion: ContentSchemaVersion;
  readonly worldMap: string;
  readonly globalDialogues: string;
  readonly tiles: string;
  readonly entities: readonly string[];
  readonly standaloneLevels: readonly string[];
}

export type MissionKind = "mine" | "cleanser" | "crenson" | "final";

export interface WorldMapNodeDocument {
  readonly id: LevelId;
  readonly level: string;
  readonly x: number;
  readonly y: number;
  readonly label: string;
  readonly area: string;
  readonly mission?: MissionKind;
  readonly iconSide?: "top" | "right" | "bottom" | "left";
  readonly initial?: boolean;
}

export interface LegacyRoadDocument {
  readonly asset:
    | "straight"
    | "turn-big-a"
    | "turn-big-b"
    | "turn-long"
    | "turn-small-a"
    | "turn-small-b";
  readonly rotate?: number;
  readonly flipX?: boolean;
  readonly flipY?: boolean;
}

export interface WorldMapEdgeDocument {
  readonly from: LevelId;
  readonly to: LevelId;
  /** Existing campaign art can pin a road sprite; new edges render as vector links. */
  readonly road?: LegacyRoadDocument;
}
export type UnlockMessageId = "welcome" | "secret-and-survival" | "ultimate";

export interface MilestoneUnlockDocument {
  readonly at: number;
  readonly next: number;
  readonly message: UnlockMessageId;
  readonly unlockLevel?: LevelId;
  readonly unlockSurvival?: true;
}

export interface WorldMapDocument {
  readonly schemaVersion: ContentSchemaVersion;
  readonly id: string;
  readonly nodes: readonly WorldMapNodeDocument[];
  readonly edges: readonly WorldMapEdgeDocument[];
  readonly milestoneUnlocks: readonly MilestoneUnlockDocument[];
}

export type DialogueSpeaker =
  "asterus" | "lumi" | "crenson" | "xenos-core" | "unknown";

export type DialogueAction =
  | "none"
  | "no-text"
  | "enable-enemy-attacks"
  | "disable-enemy-attacks"
  | "whiteout-on"
  | "whiteout-off"
  | "win"
  | "boss-vanish"
  | "boss-stop"
  | "boss-resume"
  | "activate-encounter"
  | "camera-shake"
  | "show-energy-crusher-unlock"
  | "show-gravity-slicer-unlock"
  | "show-megatron-unlock"
  | "show-barrier-wing-unlock"
  | "start-cleanser"
  | "final-shard-burst"
  | "boss-flash-and-resume"
  | "start-boss-fight"
  | "return-to-main-menu";

export interface DialogueStepDocument {
  readonly text?: string;
  readonly speaker?: DialogueSpeaker;
  readonly pose?: "default" | "alternate";
  readonly readyFrames?: number;
  readonly action?: DialogueAction;
  readonly encounter?: EncounterId;
  readonly textBox?: boolean;
}

export type DialogueCatalogDocument = Readonly<
  Record<DialogueId, readonly DialogueStepDocument[]>
>;

export interface RandomValueDocument {
  readonly minExclusive: number;
  readonly maxInclusive: number;
  readonly step?: number;
}

export type AuthoredNumber = number | RandomValueDocument;
export interface ProjectileDocument {
  readonly effect: string;
  readonly angle: number;
  readonly absolute?: boolean;
}

export interface ProjectilePatternDocument {
  readonly projectiles: readonly ProjectileDocument[];
  readonly sound?: string;
  readonly finalRepeatProjectile?: ProjectileDocument;
  readonly finalRepeatSound?: string;
}

export interface RangedBehaviorDataDocument {
  readonly channel: string;
  readonly projectilePattern: ProjectilePatternDocument;
}

export interface BossRigDocument {
  readonly shotX: number;
  readonly shotY: number;
  readonly explosionRadius: number;
}

export interface BossActionShotDocument {
  readonly pattern: string;
  readonly spacingFrames: number;
  readonly bursts: number;
  readonly resetAngle?: boolean;
}

export interface BossActionDocument {
  readonly idleFrames?: number;
  readonly shot?: BossActionShotDocument;
  readonly loopTo?: number;
}

export interface BossProjectilePatternDocument {
  readonly projectiles: readonly ProjectileDocument[];
  readonly spinDegrees?: number;
  readonly sound?: string;
  readonly missileSound?: boolean;
}

export interface BossBehaviorDataDocument {
  readonly rig: BossRigDocument;
  readonly actions: Readonly<Record<string, BossActionDocument>>;
  readonly patterns: Readonly<Record<string, BossProjectilePatternDocument>>;
  readonly warpOnRoll?: boolean;
}

export type EntityBehaviorDataDocument =
  RangedBehaviorDataDocument | BossBehaviorDataDocument;

export type EnemyBehavior =
  "ranged" | "melee" | "heavy-melee" | "mine" | "generator" | "boss";

export interface SlashDefinitionDocument {
  readonly animationOffset: number;
  readonly frameCount: number;
  readonly freezeFrame: number;
  readonly range: number;
  readonly frequency: number;
}

export interface EnemyStatsDocument {
  readonly tileSize: number;
  readonly bitmapWidth: number;
  readonly hitRadius: number;
  readonly armour?: number;
  readonly speed: number;
  readonly rollSpeed?: number;
  readonly idleFrameCount: number;
  readonly rollAnimationOffset: number;
  readonly rollFrameCount: number;
  readonly preferredDistance: AuthoredNumber;
  readonly initialActionDelay?: AuthoredNumber;
  readonly rollLimit?: number;
  readonly passesWalls?: boolean;
  readonly animationSpeed?: number;
  readonly pausesBetweenMoves?: boolean;
  readonly startsMoving?: boolean;
  readonly projectileOffset?: PixelPoint;
  readonly rewardPoints?: number;
  readonly xenosDropChance?: number;
  readonly healthDropChance?: number;
  readonly slash?: SlashDefinitionDocument;
}

export interface EntityVariantDocument {
  readonly name: string;
  readonly sprite: string;
  readonly stats: EnemyStatsDocument;
  readonly behaviorData?: EntityBehaviorDataDocument;
}

export interface EntityDefinitionDocument {
  readonly schemaVersion: ContentSchemaVersion;
  readonly id: EntityTypeId;
  readonly name: string;
  readonly behavior: EnemyBehavior;
  readonly sprite: string;
  readonly stats: EnemyStatsDocument;
  readonly behaviorData?: EntityBehaviorDataDocument;
  readonly variants?: Readonly<Record<string, EntityVariantDocument>>;
}

export interface LevelEnvironmentDocument {
  readonly zone:
    | "day"
    | "sunset"
    | "night"
    | "city-day"
    | "underground"
    | "city-night"
    | "fortress"
    | "vr";
  readonly music: string;
  readonly rain?: boolean;
  readonly fortressStage?: 1 | 2 | 3;
  /** Background asset tile ids, row-major and rectangular. */
  readonly tiles: readonly (readonly (number | null)[])[];
}
export interface TileCollisionDocument {
  readonly from: GridPoint;
  readonly to: GridPoint;
}

export interface TileDocument {
  readonly id: number;
  readonly obstacles: readonly TileCollisionDocument[];
}

export interface TileCatalogDocument {
  readonly schemaVersion: ContentSchemaVersion;
  readonly cellSize: 64;
  readonly tileSize: 512;
  readonly tiles: readonly TileDocument[];
}

export type BarrierVariant =
  | "horizontal-start"
  | "horizontal-end"
  | "horizontal-middle"
  | "vertical-start"
  | "vertical-end"
  | "vertical-middle";

export interface BarrierDocument {
  /** Top-left grid cell. */
  readonly at: GridPoint;
  readonly variant: BarrierVariant;
}

export interface BlockDocument {
  /** Top-left grid cell. */
  readonly at: GridPoint;
  readonly variant: "a" | "b";
}
export interface TriggerTargetDocument {
  /** Camera target centre in grid coordinates. Order is significant. */
  readonly at: GridPoint;
}

export interface CollisionRectangleDocument {
  /** Inclusive top-left and exclusive bottom-right grid coordinates. */
  readonly from: GridPoint;
  readonly to: GridPoint;
  /** Optional legacy background-tile bucket used by collision broad-phase. */
  readonly bucket?: GridPoint;
}

export interface EnemySpawnDocument {
  /** Spawn centre in grid coordinates. */
  readonly at: GridPoint;
  readonly entity: EntityTypeId;
  readonly count?: number;
}

export interface EncounterClearDocument {
  readonly activate?: EncounterId;
  readonly dialogue?: DialogueId;
}

export interface EncounterDocument {
  readonly id: EncounterId;
  readonly spawns?: readonly EnemySpawnDocument[];
  readonly barriers?: readonly BarrierDocument[];
  readonly onClear?: EncounterClearDocument;
}

export type TriggerMode = "encounter" | "dialogue" | "both";

export interface TriggerDocument {
  /** Top-left grid cell. */
  readonly at: GridPoint;
  readonly mode: TriggerMode;
  readonly encounter?: EncounterId;
  readonly dialogue?: DialogueId;
}

export interface GeneratorDocument {
  readonly id: GeneratorId;
  /** Generator centre in grid coordinates. */
  readonly at: GridPoint;
}

export type PickupKind = "large-xenos" | "weapon-chip" | "armour-chip";

export interface PickupDocument {
  /** Pickup centre in grid coordinates. */
  readonly at: GridPoint;
  readonly kind: PickupKind;
}

export interface BossDocument {
  readonly at: GridPoint;
  readonly entity: EntityTypeId;
  readonly variant: string;
  readonly weaponReward?: WeaponId;
  readonly weaponUnlockDialogue?: DialogueId;
  readonly lowHealthDialogue?: DialogueId;
  readonly defeatedDialogue?: DialogueId;
}

export interface CleanserDocument {
  readonly enabled: true;
}

export interface SurvivalDebriefDocument {
  /** Exclusive score ceiling; null is the final catch-all. */
  readonly belowScore: number | null;
  readonly dialogue: DialogueId;
}

export interface SurvivalDocument {
  readonly loop: readonly EncounterId[];
  readonly harderAfterWave: number;
  readonly shotWaves: readonly ShotWaveDocument[];
  readonly weaponPreset: readonly number[];
  readonly debriefs: readonly SurvivalDebriefDocument[];
}

export interface LevelCompletionDocument {
  readonly scene: "level-select" | "ending" | "title";
  readonly storyMilestone?: number;
}

export interface LevelDocument {
  readonly schemaVersion: ContentSchemaVersion;
  readonly id: LevelId;
  readonly name: string;
  readonly kind: "tutorial" | "mission" | "survival";
  readonly environment: LevelEnvironmentDocument;
  /** Player centre in grid coordinates. */
  readonly start: GridPoint;
  readonly encounters: readonly EncounterDocument[];
  readonly triggers: readonly TriggerDocument[];
  readonly generators?: readonly GeneratorDocument[];
  readonly blocks?: readonly BlockDocument[];
  readonly targets?: readonly TriggerTargetDocument[];
  readonly obstacles?: readonly CollisionRectangleDocument[];
  readonly pickups?: readonly PickupDocument[];
  readonly boss?: BossDocument;
  readonly cleanser?: CleanserDocument;
  readonly survival?: SurvivalDocument;
  readonly hiddenCollectible?: "weapon-chip" | "armour-chip";
  readonly startDialogue?: DialogueId;
  readonly firstRunDialogueOnly?: boolean;
  readonly completion: LevelCompletionDocument;
  readonly dialogues: DialogueCatalogDocument;
}

export interface GameContentDocuments {
  readonly manifest: GameManifestDocument;
  readonly worldMap: WorldMapDocument;
  readonly globalDialogues: DialogueCatalogDocument;
  readonly tiles: TileCatalogDocument;
  readonly entities: readonly EntityDefinitionDocument[];
  readonly levels: readonly LevelDocument[];
}
